import { useState, useCallback, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Composant d'autocomplétion d'adresse utilisant l'API Adresse du gouvernement français
 * Version simplifiée sans gestion de focus problématique
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Commencez à taper votre adresse...",
  className = "",
  disabled = false,
  "data-testid": testId = "address-autocomplete"
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Fermer les suggestions quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Recherche d'adresses via l'API
  const searchAddress = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5&autocomplete=1`
      );
      
      if (!response.ok) throw new Error("Erreur API");
      
      const data = await response.json();
      const results = data.features.map((feature) => ({
        label: feature.properties.label,
        name: feature.properties.name,
        housenumber: feature.properties.housenumber || "",
        street: feature.properties.street || feature.properties.name,
        postcode: feature.properties.postcode,
        city: feature.properties.city,
        department: feature.properties.context?.split(",")[0]?.trim() || 
                    feature.properties.postcode?.substring(0, 2) || ""
      }));
      
      setSuggestions(results);
      setIsOpen(results.length > 0);
    } catch (error) {
      console.error("Erreur recherche adresse:", error);
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounce de la recherche
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      searchAddress(newValue);
    }, 300);
  };

  // Sélection d'une suggestion - sans focus
  const handleSelect = useCallback((suggestion) => {
    // Fermer d'abord les suggestions
    setIsOpen(false);
    setSuggestions([]);
    
    // Puis mettre à jour les valeurs
    onChange(suggestion.label);
    
    if (onSelect) {
      // Utiliser setTimeout pour éviter les problèmes de timing
      setTimeout(() => {
        onSelect({
          address: suggestion.street ? 
            (suggestion.housenumber ? `${suggestion.housenumber} ${suggestion.street}` : suggestion.street) : 
            suggestion.name,
          postal_code: suggestion.postcode,
          city: suggestion.city,
          department: suggestion.department
        });
      }, 0);
    }
  }, [onChange, onSelect]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          type="text"
          value={value || ""}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10 h-12"
          autoComplete="off"
          data-testid={testId}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin pointer-events-none" />
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <li
              key={`suggestion-${index}`}
              onMouseDown={(e) => {
                e.preventDefault(); // Empêcher le blur de l'input
                handleSelect(suggestion);
              }}
              className="px-4 py-3 cursor-pointer hover:bg-[#E8F5E9] flex items-start gap-3"
              data-testid={`suggestion-${index}`}
            >
              <MapPin className="h-4 w-4 text-[#1A4D2E] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#1D1D1D]">{suggestion.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
